import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Table,
	TableColumnType,
	Typography,
} from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import { useApp } from '@/App';
import { useQuery } from '@tanstack/react-query';
import {
	MonitoringDocumentSearchCustomerParam,
	monitoringDocumentSearchCustomer,
} from '@/libs/fleet-api/services/monitoring-document';
import { useUserData } from '@/libs/fleet-api/hooks';
import { useSelectUserList } from '../../golive/GoliveListPage';
import { useForm } from '@/libs/antd/form';
import { FleetPagination } from '@/libs/fleet-api/fetcher';

type MonitoringDocumentListPageProps = {
	defaultParam?: MonitoringDocumentSearchCustomerParam;
};

const DEFAULT_PARAM: MonitoringDocumentSearchCustomerParam = {
	pagination: {
		pageNumber: 1,
		pageSize: 10,
	},
	search: {
		by: 'name',
		value: '',
	},
};

const MonitoringDocumentListPage: FC<MonitoringDocumentListPageProps> = ({
	defaultParam = DEFAULT_PARAM,
}) => {
	const { groupName } = useUserData() ?? {};
	const { navigate } = useApp();
	const { form, value, fieldName } =
		useForm<MonitoringDocumentSearchCustomerParam>();
	const [pagination, setPagination] = useState<FleetPagination>({
		pageNumber: 1,
		pageSize: 10,
	});
	const [param, setParam] = useState({
		...defaultParam,
		pagination,
	});

	const { data, isFetching } = useQuery({
		queryKey: ['monitoringDocumentSearchCustomer', param] as const,
		queryFn: ({ queryKey }) => {
			const [, param] = queryKey;
			if (!param) return null;
			return monitoringDocumentSearchCustomer(param);
		},
	});

	// data?.data.items; // Unused expression removed
	const selectFBDHUsers = useSelectUserList(
		{
			role: 'FBDH',
		},
		groupName,
	);

	const selectFRMUsers = useSelectUserList(
		{
			role: 'FRM',
			reportToId: value?.fbdhId,
		},
		groupName,
	);

	const selectCCDHUsers = useSelectUserList(
		{
			role: 'CCDH',
		},
		groupName,
	);

	const selectCCAUsers = useSelectUserList(
		{
			role: 'CCA',
			reportToId: value?.ccdhId,
		},
		groupName,
	);

	const extraColumnBasedOnRole: TableColumnType<{
		bmppGroupName: string;
		ccaName: null;
		ccdhName: null;
		customerId: string;
		customerName: string;
		fbdhName: null;
		frmName: null;
		processStatus: null;
	}>[] = [];

	//  adding the extra column based on the role
	if (groupName === 'FRM') {
		// DO NOTHING
	} else if (groupName === 'FBDH') {
		extraColumnBasedOnRole.push({
			title: 'FRM',
			render: (_, record) => record.frmName ?? '-',
		});
	} else if (groupName === 'CCA') {
		extraColumnBasedOnRole.push({
			title: 'FRM',
			render: (_, record) => record.frmName ?? '-',
		});
		extraColumnBasedOnRole.push({
			title: 'FBDH',
			render: (_, record) => record.fbdhName ?? '-',
		});
	} else if (groupName === 'CCDH') {
		extraColumnBasedOnRole.push({
			title: 'FRM',
			render: (_, record) => record.frmName ?? '-',
		});
		extraColumnBasedOnRole.push({
			title: 'FBDH',
			render: (_, record) => record.fbdhName ?? '-',
		});
		extraColumnBasedOnRole.push({
			title: 'CCA',
			render: (_, record) => record.ccaName ?? '-',
		});
	} else {
		extraColumnBasedOnRole.push({
			title: 'FRM',
			render: (_, record) => record.frmName ?? '-',
		});
		extraColumnBasedOnRole.push({
			title: 'FBDH',
			render: (_, record) => record.fbdhName ?? '-',
		});
		extraColumnBasedOnRole.push({
			title: 'CCA',
			render: (_, record) => record.ccaName ?? '-',
		});
		extraColumnBasedOnRole.push({
			title: 'CCDH',
			render: (_, record) => record.ccdhName ?? '-',
		});
	}

	return (
		<Fragment>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>Monitoring Dokumen</FleetTitle>
			</div>

			<Form
				form={form}
				colon={false}
				labelAlign="left"
				labelCol={{ style: { width: 160 } }}
				initialValues={param}
				onFinish={(value) => {
					setParam((prev) => {
						const newParam = {
							...prev,
							...value,
							search: {
								...prev.search,
								...value.search!,
							},
						};

						return newParam;
					});
				}}
			>
				<Row
					gutter={20}
					align="bottom"
				>
					<Col
						flex="none"
						style={{
							width: 500,
							display: 'flex',
							flexDirection: 'column',
							gap: 20,
						}}
					>
						<Form.Item
							name={fieldName(['search', 'value'])}
							label="Nama Customer"
							labelCol={{ style: { width: 150 } }}
						>
							<Input
								placeholder="Search nama customer"
								style={{
									display: 'block',
								}}
							/>
						</Form.Item>

						{(groupName === 'CCA' ||
							groupName === 'CCDH' ||
							groupName === 'FH' ||
							groupName === 'CH' ||
							groupName === 'FS' ||
							groupName === 'SECOM') && (
							<Form.Item
								label="FBDH"
								name={fieldName('fbdhId')}
							>
								<Select
									{...selectFBDHUsers}
									onSelect={(value, option) => {
										selectFBDHUsers.onSelect!(value, option);
										form.setFieldValue(fieldName('frmId'), null);
									}}
									onClear={() => {
										form.setFieldValue(fieldName('fbdhId'), null);
										form.setFieldValue(fieldName('frmId'), null);
									}}
								/>
							</Form.Item>
						)}

						{(groupName === 'FBDH' ||
							groupName === 'CCA' ||
							groupName === 'CCDH' ||
							groupName === 'FH' ||
							groupName === 'CH' ||
							groupName === 'FS' ||
							groupName === 'SECOM') && (
							<Form.Item
								label="FRM"
								name={fieldName('frmId')}
							>
								<Select
									{...selectFRMUsers}
									onClear={() => {
										form.setFieldValue(fieldName('frmId'), null);
									}}
								/>
							</Form.Item>
						)}

						{(groupName === 'FH' ||
							groupName === 'CH' ||
							groupName === 'FS' ||
							groupName === 'SECOM') && (
							<Form.Item
								label="CCDH"
								name={fieldName('ccdhId')}
							>
								<Select
									{...selectCCDHUsers}
									onSelect={(value, option) => {
										selectCCDHUsers.onSelect!(value, option);
										form.setFieldValue(fieldName('ccaId'), null);
									}}
									onClear={() => {
										form.setFieldValue(fieldName('ccdhId'), null);
										form.setFieldValue(fieldName('ccaId'), null);
									}}
								/>
							</Form.Item>
						)}

						{(groupName === 'CCDH' ||
							groupName === 'FH' ||
							groupName === 'CH' ||
							groupName === 'FS' ||
							groupName === 'SECOM') && (
							<Form.Item
								label="CCA"
								name={fieldName('ccaId')}
							>
								<Select
									{...selectCCAUsers}
									onClear={() => {
										form.setFieldValue(fieldName('ccaId'), null);
									}}
								/>
							</Form.Item>
						)}
					</Col>
					<Col flex="none">
						<Button
							type="primary"
							htmlType="submit"
							style={{ width: 250 }}
						>
							CARI
						</Button>
					</Col>
				</Row>
			</Form>

			<Divider />

			<Typography.Title
				level={3}
				style={{
					marginBottom: 20,
				}}
			>
				List Debitur
			</Typography.Title>

			<Table
				scroll={{ x: 'max-content' }}
				loading={isFetching}
				bordered
				dataSource={
					data?.data?.items?.map?.((item, i) => {
						return {
							...item,
							key: i,
						};
					}) ?? []
				}
				columns={[
					{
						title: 'No',
						width: 1,
						align: 'center',
						render: (_, __, index) => {
							const { pageNumber, pageSize } = pagination;
							return pageSize * (pageNumber - 1) + (index + 1);
						},
					},
					{
						title: 'Customer ID',
						render: (_, record) => record.customerId ?? '-',
					},
					{
						title: 'Nama Customer',
						render: (_, record) => record.customerName ?? '-',
					},
					{
						title: 'Group BMPP',
						render: (_, record) => record.bmppGroupName ?? '-',
					},
					...extraColumnBasedOnRole,
					{
						title: 'Status',
						render: (_, record) => record.processStatus ?? '-',
					},
					{
						fixed: 'right',
						title: 'Detail Monitoring Dokumen',
						align: 'center',
						render: (_, { customerId }) => {
							if (!customerId) return null;
							return (
								<a
									onClick={(e) => {
										e.preventDefault();
										navigate('MonitoringDocumentDetailPage', {
											customerId,
											param: {
												pagination: {
													pageNumber: 1,
													pageSize: 10,
												},
											},
										});
									}}
								>
									<ArrowRightOutlined />
								</a>
							);
						},
					},
				]}
				pagination={{
					total: data?.data.totalCount,
					current: pagination.pageNumber,
					pageSize: pagination.pageSize,
					showSizeChanger: false,
					onChange: (pageNumber, pageSize) => {
						setPagination((current) => ({
							pageSize,
							pageNumber: current.pageSize !== pageSize ? 1 : pageNumber,
						}));
						setParam((current) => ({
							...current,
							pagination: {
								pageSize,
								pageNumber:
									current.pagination?.pageSize !== pageSize ? 1 : pageNumber,
							},
						}));
					},
				}}
			/>
		</Fragment>
	);
};

export default MonitoringDocumentListPage;
